<template>
    <div class="branch-merchant-setup">
        <div class="setup-header">
            <h4>Select Branch & Merchant</h4>
            <p class="text-muted">Choose an existing branch and merchant or create new ones</p>
        </div>

        <div class="setup-type-selector">
            <div class="radio-group">
                <label class="radio-option" :class="{ active: setupType === 'existing' }">
                    <input type="radio" value="existing" v-model="setupType" />
                    <div class="radio-content">
                        <i class="fas fa-list-ul"></i>
                        <span>Use Existing</span>
                    </div>
                </label>
                <label class="radio-option" :class="{ active: setupType === 'new' }">
                    <input type="radio" value="new" v-model="setupType" />
                    <div class="radio-content">
                        <i class="fas fa-plus-circle"></i>
                        <span>Create New</span>
                    </div>
                </label>
            </div>
        </div>

        <!-- Existing Selection -->
        <div v-if="setupType === 'existing'" class="existing-selection">
            <div class="form-group">
                <label for="branch-select">Select Branch <span class="required">*</span></label>
                <select id="branch-select" v-model="formData.branch_id" class="form-control" @change="onBranchChange">
                    <option value="">-- Select Branch --</option>
                    <option v-for="branch in availableBranches" :key="branch.branch_id" :value="branch.branch_id">
                        {{ branch.branch_name }} ({{ branch.branch_code }})
                    </option>
                </select>
            </div>

            <div class="form-group" v-if="formData.branch_id">
                <label for="merchant-select">Select Merchant <span class="required">*</span></label>
                <select id="merchant-select" v-model="formData.merchant_id" class="form-control">
                    <option value="">-- Select Merchant --</option>
                    <option v-for="merchant in selectedBranchMerchants" :key="merchant.merchant_id"
                        :value="merchant.merchant_id">
                        {{ merchant.merchant_name }} ({{ merchant.merchant_code }})
                    </option>
                </select>
            </div>
        </div>

        <!-- Create New -->
        <div v-if="setupType === 'new'" class="create-new-form">
            <!-- Sub Step 1: Branch Information -->
            <div class="branch-section">
                <div class="section-title">
                    <i class="fas fa-building"></i>
                    <h5>New Branch Information</h5>
                </div>

                <o-form autocomplete="off">
                    <div class="row">
                        <div class="col-md-6">
                            <o-form-group title="Name" name="branch-name">
                                <span class="text-danger"> *</span>
                                <o-input-text name="branch-name" v-model="formData.branch.name" type="text"
                                    :required="true" autocomplete="off" placeholder="Enter name.."
                                    :disabled="subStep === 'merchant'" />
                            </o-form-group>
                        </div>
                        <div class="col-md-6">
                            <o-form-group title="Code" name="branch-code">
                                <span class="text-danger"> *</span>
                                <o-input-text name="branch-code" v-model="formData.branch.code" type="text"
                                    :required="true" autocomplete="off" placeholder="Enter code.."
                                    :disabled="subStep === 'merchant'" :class="{ 'is-invalid': getErrorMessage('branch.code') }" />
                                <span class="text-danger small mt-1 d-block" v-if="getErrorMessage('branch.code')">
                                    <i class="fas fa-exclamation-circle me-1"></i>
                                    {{ getErrorMessage('branch.code') }}
                                </span>
                            </o-form-group>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-6">
                            <o-form-group title="Country" name="branch-country">
                                <span class="text-danger"> *</span>
                                <o-select v-model="formData.branch.country" :options="countryOptions" autocomplete="off"
                                    :disabled="subStep === 'merchant'"></o-select>
                            </o-form-group>
                        </div>
                        <div class="col-md-6">
                            <o-form-group title="State/Province" name="branch-state">
                                <span class="text-danger"> *</span>
                                <o-input-text name="branch-state" v-model="formData.branch.state" type="text"
                                    :required="true" autocomplete="off" placeholder="Enter State.."
                                    :disabled="subStep === 'merchant'" />
                            </o-form-group>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-6">
                            <o-form-group title="City/Area" name="branch-city">
                                <span class="text-danger"> *</span>
                                <o-input-text name="branch-city" v-model="formData.branch.city" type="text"
                                    :required="true" autocomplete="off" placeholder="Enter City.."
                                    :disabled="subStep === 'merchant'" />
                            </o-form-group>
                        </div>
                        <div class="col-md-6">
                            <o-form-group title="Postcode" name="branch-postcode">
                                <span class="text-danger"> *</span>
                                <o-input-text name="branch-postcode" v-model="formData.branch.postcode" type="text"
                                    :required="true" autocomplete="off" placeholder="Enter Postcode.."
                                    :disabled="subStep === 'merchant'" />
                            </o-form-group>
                        </div>
                    </div>

                    <o-form-group title="Address" name="branch-address">
                        <span class="text-danger"> *</span>
                        <o-textarea name="branch-address" v-model="formData.branch.address"
                            placeholder="Enter Address.." style="height: 45px" autocomplete="off"
                            :disabled="subStep === 'merchant'"></o-textarea>
                    </o-form-group>

                    <div class="row">
                        <div class="col-md-6">
                            <o-form-group title="Phone" name="branch-phone">
                                <span class="text-danger"> *</span>
                                <div class="d-flex">
                                    <div style="width: 25%;" v-if="!branchUnknownCountry">
                                        <o-input-text name="branch-countryCode" v-model="branchCountryCode" type="text"
                                            :required="true" :readonly="true" :disabled="subStep === 'merchant'"
                                            autocomplete="off" />
                                    </div>
                                    <div style="width: 100%;">
                                        <o-input-text name="branch-phone" v-model="formData.branch.phone" type="text"
                                            :required="true" :numbers-only="true" autocomplete="off"
                                            placeholder="Enter Phone.." :disabled="subStep === 'merchant'" />
                                    </div>
                                </div>
                            </o-form-group>
                        </div>
                        <div class="col-md-6">
                            <o-form-group title="Fax" name="branch-fax">
                                <span class="text-danger"> *</span>
                                <o-input-text mask="fax" name="branch-fax" v-model="formData.branch.fax" type="text"
                                    autocomplete="off" placeholder="Enter Fax.." :disabled="subStep === 'merchant'" />
                            </o-form-group>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-6">
                            <o-form-group title="Website URL" name="branch-website">
                                <o-input-text name="branch-website" v-model="formData.branch.website" type="text"
                                    autocomplete="off" placeholder="Enter Website URL.."
                                    :disabled="subStep === 'merchant'" />
                            </o-form-group>
                        </div>
                        <div class="col-md-6">
                            <o-form-group title="Commerce Site" name="branch-commerce">
                                <o-input-text name="branch-commerce" v-model="formData.branch.commerce_site" type="text"
                                    autocomplete="off" placeholder="Enter Commerce Site.."
                                    :disabled="subStep === 'merchant'" />
                            </o-form-group>
                        </div>
                    </div>

                    <o-form-group title="Logo" name="branch-logo">
                        <o-image-input name="branch-logo" id="branch-logo" :imageurl="branchImageUrl"
                            @selectImage="imageHandlingBranch" :disabled="subStep === 'merchant'"></o-image-input>
                    </o-form-group>
                </o-form>

                <div class="form-actions" v-if="subStep === 'branch'">
                    <button @click="saveBranch" class="btn-black">
                        Save Branch
                    </button>
                </div>
            </div>

            <!-- Sub Step 2: Merchant Information -->
            <div v-if="subStep === 'merchant'" class="merchant-section mt-4 pt-4 border-top">
                <div class="section-title">
                    <i class="fas fa-store"></i>
                    <h5>New Merchant Information</h5>
                </div>

                <o-form autocomplete="off">
                    <div class="row">
                        <div class="col-md-6">
                            <o-form-group title="Name" name="merchant-name">
                                <span class="text-danger"> *</span>
                                <o-input-text name="merchant-name" v-model="formData.merchant.name" type="text"
                                    :required="true" autocomplete="off" placeholder="Enter name.."
                                    :disabled="merchantSaved" />
                            </o-form-group>
                        </div>
                        <div class="col-md-6">
                            <o-form-group title="Code" name="merchant-code">
                                <span class="text-danger"> *</span>
                                <o-input-text name="merchant-code" v-model="formData.merchant.code" type="text"
                                    :required="true" autocomplete="off" placeholder="Enter code.."
                                    :disabled="merchantSaved" :class="{ 'is-invalid': getErrorMessage('merchant.code') }" />
                                <span class="text-danger small mt-1 d-block" v-if="getErrorMessage('merchant.code')">
                                    <i class="fas fa-exclamation-circle me-1"></i>
                                    {{ getErrorMessage('merchant.code') }}
                                </span>
                            </o-form-group>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-6">
                            <o-form-group title="Country" name="merchant-country">
                                <span class="text-danger"> *</span>
                                <o-select v-model="formData.merchant.country" :options="countryOptions"
                                    autocomplete="off" :disabled="merchantSaved"></o-select>
                            </o-form-group>
                        </div>
                        <div class="col-md-6">
                            <o-form-group title="State/Province" name="merchant-state">
                                <span class="text-danger"> *</span>
                                <o-input-text name="merchant-state" v-model="formData.merchant.state" type="text"
                                    :required="true" autocomplete="off" placeholder="Enter State.."
                                    :disabled="merchantSaved" />
                            </o-form-group>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-6">
                            <o-form-group title="City/Area" name="merchant-city">
                                <span class="text-danger"> *</span>
                                <o-input-text name="merchant-city" v-model="formData.merchant.city" type="text"
                                    :required="true" autocomplete="off" placeholder="Enter City.."
                                    :disabled="merchantSaved" />
                            </o-form-group>
                        </div>
                        <div class="col-md-6">
                            <o-form-group title="Postcode" name="merchant-postcode">
                                <span class="text-danger"> *</span>
                                <o-input-text name="merchant-postcode" v-model="formData.merchant.postcode" type="text"
                                    :required="true" autocomplete="off" placeholder="Enter Postcode.."
                                    :disabled="merchantSaved" />
                            </o-form-group>
                        </div>
                    </div>

                    <o-form-group title="Address" name="merchant-address">
                        <span class="text-danger"> *</span>
                        <o-textarea name="merchant-address" v-model="formData.merchant.address"
                            placeholder="Enter Address.." style="height: 45px" autocomplete="off"
                            :disabled="merchantSaved"></o-textarea>
                    </o-form-group>

                    <div class="row">
                        <div class="col-md-6">
                            <o-form-group title="Phone" name="merchant-phone">
                                <span class="text-danger"> *</span>
                                <div class="d-flex">
                                    <div style="width: 25%;" v-if="!merchantUnknownCountry">
                                        <o-input-text name="merchant-countryCode" v-model="merchantCountryCode"
                                            type="text" :required="true" :readonly="true" :disabled="merchantSaved"
                                            autocomplete="off" />
                                    </div>
                                    <div style="width: 100%;">
                                        <o-input-text name="merchant-phone" v-model="formData.merchant.phone"
                                            type="text" :required="true" :numbers-only="true" autocomplete="off"
                                            placeholder="Enter Phone.." :disabled="merchantSaved" />
                                    </div>
                                </div>
                            </o-form-group>
                        </div>
                        <div class="col-md-6">
                            <o-form-group title="Fax" name="merchant-fax">
                                <span class="text-danger"> *</span>
                                <o-input-text mask="fax" name="merchant-fax" v-model="formData.merchant.fax" type="text"
                                    autocomplete="off" placeholder="Enter Fax.." :disabled="merchantSaved" />
                            </o-form-group>
                        </div>
                    </div>

                    <o-form-group title="Website URL" name="merchant-website">
                        <o-input-text name="merchant-website" v-model="formData.merchant.website" type="text"
                            autocomplete="off" placeholder="Enter Website URL.." :disabled="merchantSaved" />
                    </o-form-group>

                    <o-form-group title="Logo" name="merchant-logo">
                        <o-image-input name="merchant-logo" id="merchant-logo" :imageurl="merchantImageUrl"
                            @selectImage="imageHandlingMerchant" :disabled="merchantSaved"></o-image-input>
                    </o-form-group>
                </o-form>

                <div class="form-actions">
                    <button @click="backToBranch" class="btn-secondary me-2" v-if="!merchantSaved">
                        Edit Branch
                    </button>
                    <button @click="saveMerchant" class="btn-black" v-if="!merchantSaved">
                        Save Merchant
                    </button>
                    <button @click="editMerchant" class="btn-secondary" v-if="merchantSaved">
                        Edit Merchant
                    </button>
                </div>

                <div v-if="merchantSaved" class="alert alert-success mt-3">
                    <i class="fas fa-check-circle me-2"></i>
                    Branch and Merchant succesfully saved, click Next button to continue setup connection.
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { OForm, OFormGroup, OInputText, OTextarea, OSelect, OImageInput } from '@/components/OmnihControl'
import { useToast } from '@/composables/useToast'

export default {
    name: 'BranchMerchantSetup',
    components: {
        OForm,
        OFormGroup,
        OInputText,
        OTextarea,
        OSelect,
        OImageInput
    },
    props: {
        modelValue: {
            type: Object,
            default: () => ({})
        },
        serverErrors: {
            type: Object,
            default: () => ({})
        }
    },
    emits: ['update:modelValue', 'valid'],
    setup(props, { emit }) {
        const store = useStore()
        const { showToast } = useToast()

        const setupType = ref('existing')
        const subStep = ref('branch')
        const merchantSaved = ref(false)
        const countryOptions = ref([])
        const countries = ref([])
        const countriesData = ref([])

        const branchCountryCode = ref('')
        const branchUnknownCountry = ref(false)
        const merchantCountryCode = ref('')
        const merchantUnknownCountry = ref(false)
        const branchImageUrl = ref('')
        const merchantImageUrl = ref('')

        const formData = ref({
            setup_type: 'existing',
            branch_id: '',
            merchant_id: '',
            branch: {
                name: '',
                code: '',
                address: '',
                city: '',
                state: '',
                country: '',
                postcode: '',
                phone: '',
                fax: '',
                website: '',
                commerce_site: '',
                logo: ''
            },
            merchant: {
                name: '',
                code: '',
                address: '',
                city: '',
                state: '',
                country: '',
                postcode: '',
                phone: '',
                fax: '',
                website: '',
                logo: ''
            }
        })

        const availableBranches = computed(() => store.getters['ConnectionApps/branches'])

        const selectedBranchMerchants = computed(() => {
            if (!formData.value.branch_id) return []
            const branch = availableBranches.value.find(b => b.branch_id === formData.value.branch_id)
            return branch?.merchants || []
        })

        watch(setupType, (newType) => {
            formData.value.setup_type = newType
            formData.value.branch_id = ''
            formData.value.merchant_id = ''
            subStep.value = 'branch'
            merchantSaved.value = false
            emitUpdate()
        })

        watch(formData, () => {
            if (setupType.value === 'existing') {
                emitUpdate()
            }
        }, { deep: true })

        watch(() => formData.value.branch.country, () => {
            updatePhoneCode('branch')
        })

        watch(() => formData.value.merchant.country, () => {
            updatePhoneCode('merchant')
        })

        /**
         * Handle branch selection change
         * Resets merchant selection and emits update
         */
        function onBranchChange() {
            formData.value.merchant_id = ''
            emitUpdate()
        }

        /**
         * Emit update event with current form data
         * Validates form if in 'existing' mode
         */
        function emitUpdate() {
            emit('update:modelValue', formData.value)
            if (setupType.value === 'existing') {
                validateForm()
            }
        }

        /**
         * Load available countries from the store
         * Populates country options for dropdowns
         * 
         * @returns {Promise<void>}
         */
        async function loadCountries() {
            try {
                const response = await store.dispatch('Countries/read')
                countriesData.value = response
                countries.value = response.map(country => ({ value: country.id, text: country.name }))
                countryOptions.value = countries.value
            } catch (err) {
                console.error(err)
            }
        }

        /**
         * Update phone code based on selected country
         * 
         * @param {string} type 'branch' or 'merchant'
         */
        function updatePhoneCode(type) {
            const data = type === 'branch' ? formData.value.branch : formData.value.merchant
            const countryId = parseInt(data.country)

            if (!countriesData.value || countriesData.value.length === 0) return

            const country = countriesData.value.find(c => c.id === countryId)

            if (country && country.phone_code) {
                const formattedCode = `${country.phone_code} `
                if (type === 'branch') {
                    branchUnknownCountry.value = false
                    branchCountryCode.value = formattedCode
                } else {
                    merchantUnknownCountry.value = false
                    merchantCountryCode.value = formattedCode
                }
            } else {
                if (type === 'branch') branchUnknownCountry.value = true
                else merchantUnknownCountry.value = true
            }
        }

        /**
         * Handle branch logo selection
         * 
         * @param {string} img Image URL/Data
         */
        function imageHandlingBranch(img) {
            formData.value.branch.logo = img
        }

        /**
         * Handle merchant logo selection
         * 
         * @param {string} img Image URL/Data
         */
        function imageHandlingMerchant(img) {
            formData.value.merchant.logo = img
        }

        /**
         * Validate branch form data
         * 
         * @returns {boolean} True if valid, false otherwise
         */
        function validateBranch() {
            const b = formData.value.branch
            if (!b.name || !b.code || !b.country || !b.state || !b.city || !b.postcode || !b.address || !b.phone || !b.fax) {
                showToast({
                    type: 'error',
                    title: 'Validation Error',
                    message: 'Please fill in all required fields marked with *'
                })
                return false
            }
            return true
        }

        /**
         * Validate merchant form data
         * 
         * @returns {boolean} True if valid, false otherwise
         */
        function validateMerchant() {
            const m = formData.value.merchant
            if (!m.name || !m.code || !m.country || !m.state || !m.city || !m.postcode || !m.address || !m.phone || !m.fax) {
                showToast({
                    type: 'error',
                    title: 'Validation Error',
                    message: 'Please fill in all required fields marked with *'
                })
                return false
            }
            return true
        }

        /**
         * Save branch information locally
         * Validates branch data, auto-fills merchant data, and moves to merchant step
         */
        function saveBranch() {
            if (validateBranch()) {
                const b = formData.value.branch
                formData.value.merchant = {
                    ...formData.value.merchant,
                    country: b.country,
                    state: b.state,
                    city: b.city,
                    postcode: b.postcode,
                    address: b.address,
                    phone: b.phone,
                    fax: b.fax
                }

                setTimeout(() => updatePhoneCode('merchant'), 100)

                subStep.value = 'merchant'
                setTimeout(() => {
                    const merchantSection = document.querySelector('.merchant-section')
                    if (merchantSection) {
                        merchantSection.scrollIntoView({ behavior: 'smooth' })
                    }
                }, 100)
            }
        }

        /**
         * Go back to branch editing step
         */
        function backToBranch() {
            subStep.value = 'branch'
        }

        /**
         * Enable editing of merchant information
         * Resets saved state
         */
        function editMerchant() {
            merchantSaved.value = false
            emit('valid', false)
        }

        /**
         * Save merchant information locally
         * Validates merchant data, prepares payload with formatted phones, and emits update
         */
        function saveMerchant() {
            if (validateMerchant()) {
                const payload = JSON.parse(JSON.stringify(formData.value))

                if (!branchUnknownCountry.value) {
                    payload.branch.phone = (branchCountryCode.value + formData.value.branch.phone).replace(/\s+/g, '')
                }
                if (!merchantUnknownCountry.value) {
                    payload.merchant.phone = (merchantCountryCode.value + formData.value.merchant.phone).replace(/\s+/g, '')
                }

                payload.branch.country = String(payload.branch.country)
                payload.merchant.country = String(payload.merchant.country)
                const fixUrl = (url) => {
                    if (!url) return null
                    if (!/^https?:\/\//i.test(url)) {
                        return 'https://' + url
                    }
                    return url
                }

                payload.branch.website = fixUrl(payload.branch.website)
                payload.branch.commerce_site = fixUrl(payload.branch.commerce_site)
                payload.merchant.website = fixUrl(payload.merchant.website)

                emit('update:modelValue', payload)
                emit('valid', true)

                merchantSaved.value = true
                setTimeout(() => {
                    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
                }, 100)
            }
        }

        /**
         * Validate the entire form based on setup type
         * 
         * @returns {boolean} True if valid, false otherwise
         */
        function validateForm() {
            if (setupType.value === 'existing') {
                if (!formData.value.branch_id) {
                    emit('valid', false)
                    return false
                }
                if (!formData.value.merchant_id) {
                    emit('valid', false)
                    return false
                }
                emit('valid', true)
                return true
            }
            return false
        }

        function getErrorMessage(field) {
            if (props.serverErrors && props.serverErrors[field]) {
                return formatErrorMessage(props.serverErrors[field][0])
            }
            return null
        }

        function formatErrorMessage(msg) {
            if (!msg) return ''
            let cleanMsg = msg
            
            const replacements = {
                'branch.code': 'Branch Code',
                'merchant.code': 'Merchant Code',
                'branch.name': 'Branch Name',
                'merchant.name': 'Merchant Name',
            }

            Object.keys(replacements).forEach(key => {
                cleanMsg = cleanMsg.replace(key, replacements[key])
            })

            if (cleanMsg.startsWith('The ')) {
                cleanMsg = cleanMsg.substring(4)
            }
            
            return cleanMsg.charAt(0).toUpperCase() + cleanMsg.slice(1)
        }

        onMounted(() => {
            store.dispatch('ConnectionApps/getAvailableBranches')
            loadCountries()

            if (props.modelValue && Object.keys(props.modelValue).length > 0) {
                const val = props.modelValue
                formData.value.setup_type = val.setup_type || 'existing'
                formData.value.branch_id = val.branch_id || ''
                formData.value.merchant_id = val.merchant_id || ''

                if (val.branch) {
                    Object.assign(formData.value.branch, val.branch)
                }
                if (val.merchant) {
                    Object.assign(formData.value.merchant, val.merchant)
                }

                setupType.value = formData.value.setup_type
            }
        })

        return {
            setupType,
            subStep,
            formData,
            availableBranches,
            selectedBranchMerchants,
            countryOptions,
            branchCountryCode,
            branchUnknownCountry,
            merchantCountryCode,
            merchantUnknownCountry,
            branchImageUrl,
            merchantImageUrl,
            merchantSaved,
            onBranchChange,
            saveBranch,
            backToBranch,
            saveMerchant,
            editMerchant,
            imageHandlingBranch,
            imageHandlingMerchant,
            getErrorMessage
        }
    }
}
</script>

<style scoped>
.branch-merchant-setup {
    padding: 1.5rem;
}

.setup-header {
    margin-bottom: 2rem;
}

.setup-header h4 {
    margin-bottom: 0.5rem;
    color: #111827;
}

.text-muted {
    color: #6b7280;
    font-size: 0.875rem;
}

.setup-type-selector {
    margin-bottom: 2rem;
}

.radio-group {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
}

.radio-option {
    position: relative;
    cursor: pointer;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    padding: 1.5rem;
    transition: all 0.2s ease;
    background: white;
}

.radio-option:hover {
    border-color: var(--primary-color, #F93E3E);
    box-shadow: 0 2px 8px color-mix(in srgb, var(--primary-color), transparent 90%);
}

.radio-option.active {
    border-color: var(--primary-color, #F93E3E);
    background: color-mix(in srgb, var(--primary-color), transparent 95%);
}

.radio-option input[type="radio"] {
    position: absolute;
    opacity: 0;
}

.radio-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-weight: 500;
    color: #374151;
}

.radio-content i {
    font-size: 1.25rem;
    color: var(--primary-color, #F93E3E);
}

.section-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid #f3f4f6;
}

.section-title i {
    color: var(--primary-color, #F93E3E);
}

.section-title h5 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
}

.form-group {
    margin-bottom: 1.25rem;
}

.form-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #374151;
    font-size: 0.875rem;
}

.required {
    color: #dc2626;
}

.form-control {
    width: 100%;
    padding: 0.625rem 0.875rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    transition: all 0.2s ease;
}

.form-control:focus {
    outline: none;
    border-color: var(--primary-color, #F93E3E);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary-color), transparent 85%);
}

.form-actions {
    margin-top: 2rem;
    display: flex;
    justify-content: flex-start;
}

.btn-black {
    background: var(--primary-color, #F93E3E);
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 500;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-black:hover {
    filter: brightness(0.9);
}

.btn-secondary {
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-secondary:hover {
    background: #f9fafb;
    border-color: #9ca3af;
}

.alert {
    padding: 0.875rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
}

.alert-danger {
    background: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
}

.existing-selection,
.create-new-form {
    background: #f9fafb;
    padding: 1.5rem;
    border-radius: 12px;
}
</style>
